import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Row, Col, Alert } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faRocket,
  faPaperPlane,
  faLock,
  faClock,
  faCheckCircle,
  faTimesCircle,
} from "@fortawesome/free-solid-svg-icons";
import { DemoRequest } from "../../services/app/DemoRequest";
import "../../styles/app/DemoForm.css";
import { useTranslation } from "react-i18next";

interface DemoFormProps {
  show: boolean;
  onClose: () => void;
}

const lang = process.env.REACT_APP_DEFAULT_LANGUAGE
  ? process.env.REACT_APP_DEFAULT_LANGUAGE
  : "";

const DemoForm: React.FC<DemoFormProps> = ({ show, onClose }) => {
  const { t } = useTranslation();
  // Estados para los inputs
  const [fullName, setFullName] = useState("");
  const [corporateEmail, setCorporateEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [websiteUrl, setWebsiteUrl] = useState("");

  // Estados para los selects
  const [jobTitle, setJobTitle] = useState("");
  const [industry, setIndustry] = useState("");
  const [companySize, setCompanySize] = useState("");
  const [monthlyCustomerQueries, setMonthlyCustomerQueries] = useState("");

  // Estado para el textarea
  const [additionalComments, setAdditionalComments] = useState("");

  // Estados para manejar la respuesta
  const [isSuccess, setIsSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  const [fullNameError, setFullNameError] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [websiteUrlError, setWebsiteUrlError] = useState("");

  // Resetear el formulario cuando el modal se cierra completamente
  useEffect(() => {
    if (!show && !isClosing) {
      resetForm();
    }
  }, [show, isClosing]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMessage("");

    try {
      const response = await DemoRequest({
        fullName,
        corporateEmail,
        phone,
        companyName,
        jobTitle,
        industry,
        companySize,
        monthlyCustomerQueries,
        additionalComments,
        websiteUrl, // Add this line
        language: lang,
      });

      if (response.statusCode === 200) {
        setIsSuccess(true);
      } else {
        setErrorMessage(t("demoForm.submitError"));
      }
    } catch (error) {
      setErrorMessage(t("demoForm.serverError"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFullName("");
    setCorporateEmail("");
    setPhone("");
    setCompanyName("");
    setWebsiteUrl("");
    setJobTitle("");
    setIndustry("");
    setCompanySize("");
    setMonthlyCustomerQueries("");
    setAdditionalComments("");
    setIsSuccess(false);
    setErrorMessage("");
  };

  const handleClose = () => {
    // Primero marcamos que estamos cerrando
    setIsClosing(true);
    // Llamamos a onClose para iniciar la animación de cierre
    onClose();
    // El reset del formulario se hará en el useEffect cuando show sea false
  };

  const validateFullName = (value: string) => {
    const regex = /^[a-zA-ZÁÉÍÓÚáéíóúÑñ\s]*$/;

    if (!regex.test(value)) {
      setFullNameError(t("demoForm.fullNameError"));
    } else {
      setFullNameError("");
    }

    setFullName(value);
  };

  const validatePhone = (value: string) => {
    if (!/^[\d+]*$/.test(value)) {
      setPhoneError(t("demoForm.phoneError"));
    } else {
      setPhoneError("");
    }
    setPhone(value);
  };

  const validateWebsiteUrl = (value: string) => {
    if (
      value &&
      !/^(https?):\/\/([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/i.test(value)
    ) {
      setWebsiteUrlError(t("demoForm.websiteUrlError"));
    } else {
      setWebsiteUrlError("");
    }
    setWebsiteUrl(value);
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      size="lg"
      backdrop="static"
      contentClassName="demo-form-overlay"
      onExited={() => {
        // Cuando la animación de cierre ha terminado, marcamos que ya no estamos cerrando
        setIsClosing(false);
      }}
    >
      <Modal.Header closeButton closeVariant="white" className="border-0" />

      <Modal.Body className="demo-form-content">
        {isSuccess ? (
          <div className="text-center">
            <FontAwesomeIcon
              icon={faCheckCircle}
              className="text-success mb-3"
              size="4x"
            />
            <h5 className="mb-3">{t("demoForm.successTitle")}</h5>
            <p>{t("demoForm.successMessage")}</p>
            <Button
              variant="primary"
              onClick={handleClose}
              className="demo-form-btn"
            >
              {t("demoForm.closeButton")}
            </Button>
          </div>
        ) : (
          <>
            <h5 className="text-center mb-3">
              <FontAwesomeIcon icon={faRocket} className="me-2" />
              {t("demoForm.title")}
            </h5>
            <p className="text-center mb-4">{t("demoForm.subtitle")}</p>

            <div className="demo-form-benefits bg-dark text-white rounded p-3 mb-4">
              <h5 className="mb-3">{t("demoForm.benefitsTitle")}</h5>
              <ul className="mb-0">
                <li>{t("demoForm.benefit1")}</li>
                <li>{t("demoForm.benefit2")}</li>
                <li>{t("demoForm.benefit3")}</li>
                <li>{t("demoForm.benefit4")}</li>
                <li>{t("demoForm.benefit5")}</li>
                <li>{t("demoForm.benefit6")}</li>
              </ul>
            </div>

            <Form onSubmit={handleSubmit}>
              <Row className="mb-3">
                {/* Inputs */}
                <Col md={6}>
                  <Form.Group controlId="nombreCompleto" className="mb-3">
                    <Form.Label>{t("demoForm.fullNameLabel")}</Form.Label>
                    <Form.Control
                      className="demo-form-input"
                      type="text"
                      value={fullName}
                      onChange={(e) => validateFullName(e.target.value)}
                      maxLength={100}
                      required
                      disabled={isSubmitting}
                    />
                    {fullNameError && (
                      <Form.Text className="text-danger">
                        {fullNameError}
                      </Form.Text>
                    )}
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group controlId="email" className="mb-3">
                    <Form.Label>{t("demoForm.emailLabel")}</Form.Label>
                    <Form.Control
                      className="demo-form-input"
                      type="email"
                      value={corporateEmail}
                      onChange={(e) => setCorporateEmail(e.target.value)}
                      maxLength={100}
                      required
                      disabled={isSubmitting}
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group controlId="telefono" className="mb-3">
                    <Form.Label>{t("demoForm.phoneLabel")}</Form.Label>
                    <Form.Control
                      className="demo-form-input"
                      type="tel"
                      value={phone}
                      onChange={(e) => validatePhone(e.target.value)}
                      maxLength={50}
                      required
                      disabled={isSubmitting}
                    />
                    {phoneError && (
                      <Form.Text className="text-danger">
                        {phoneError}
                      </Form.Text>
                    )}
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group controlId="empresa" className="mb-3">
                    <Form.Label>{t("demoForm.companyNameLabel")}</Form.Label>
                    <Form.Control
                      className="demo-form-input"
                      type="text"
                      value={companyName}
                      onChange={(e) => setCompanyName(e.target.value)}
                      maxLength={100}
                      required
                      disabled={isSubmitting}
                    />
                  </Form.Group>
                </Col>
                {/* Selects */}
                <Col md={6}>
                  <Form.Group controlId="cargo" className="mb-3">
                    <Form.Label>{t("demoForm.jobTitleLabel")}</Form.Label>
                    <Form.Select
                      className="demo-form-select"
                      value={jobTitle}
                      onChange={(e) => setJobTitle(e.target.value)}
                      required
                      disabled={isSubmitting}
                    >
                      <option value="">{t("demoForm.selectOption")}</option>
                      <option value="CEO/Fundador">
                        {t("demoForm.jobTitleOptions.ceo")}
                      </option>
                      <option value="Gerente General">
                        {t("demoForm.jobTitleOptions.generalManager")}
                      </option>
                      <option value="Director de Operaciones">
                        {t("demoForm.jobTitleOptions.coo")}
                      </option>
                      <option value="Gerente de Atención al Cliente">
                        {t("demoForm.jobTitleOptions.customerServiceManager")}
                      </option>
                      <option value="Director de Marketing">
                        {t("demoForm.jobTitleOptions.marketingDirector")}
                      </option>
                      <option value="Gerente de Ventas">
                        {t("demoForm.jobTitleOptions.salesManager")}
                      </option>
                      <option value="Propietario">
                        {t("demoForm.jobTitleOptions.owner")}
                      </option>
                      <option value="Otro">
                        {t("demoForm.jobTitleOptions.other")}
                      </option>
                    </Form.Select>
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group controlId="industria" className="mb-3">
                    <Form.Label>{t("demoForm.industryLabel")}</Form.Label>
                    <Form.Select
                      className="demo-form-select"
                      value={industry}
                      onChange={(e) => setIndustry(e.target.value)}
                      required
                      disabled={isSubmitting}
                    >
                      <option value="">{t("demoForm.selectOption")}</option>
                      <option value="Servicios Profesionales">
                        {t("demoForm.industryOptions.professionalServices")}
                      </option>
                      <option value="Retail/Comercio">
                        {t("demoForm.industryOptions.retail")}
                      </option>
                      <option value="Restaurantes/Hospitalidad">
                        {t("demoForm.industryOptions.hospitality")}
                      </option>
                      <option value="Salud/Medicina">
                        {t("demoForm.industryOptions.health")}
                      </option>
                      <option value="Educación">
                        {t("demoForm.industryOptions.education")}
                      </option>
                      <option value="Finanzas/Seguros">
                        {t("demoForm.industryOptions.finance")}
                      </option>
                      <option value="Inmobiliario">
                        {t("demoForm.industryOptions.realEstate")}
                      </option>
                      <option value="Tecnología">
                        {t("demoForm.industryOptions.technology")}
                      </option>
                      <option value="Manufactura">
                        {t("demoForm.industryOptions.manufacturing")}
                      </option>
                      <option value="Otro">
                        {t("demoForm.industryOptions.other")}
                      </option>
                    </Form.Select>
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group controlId="tamanoEmpresa" className="mb-3">
                    <Form.Label>{t("demoForm.companySizeLabel")}</Form.Label>
                    <Form.Select
                      className="demo-form-select"
                      value={companySize}
                      onChange={(e) => setCompanySize(e.target.value)}
                      required
                      disabled={isSubmitting}
                    >
                      <option value="">{t("demoForm.selectOption")}</option>
                      <option value="1-5 empleados">
                        {t("demoForm.companySizeOptions.small1")}
                      </option>
                      <option value="6-20 empleados">
                        {t("demoForm.companySizeOptions.small2")}
                      </option>
                      <option value="21-50 empleados">
                        {t("demoForm.companySizeOptions.medium1")}
                      </option>
                      <option value="51-100 empleados">
                        {t("demoForm.companySizeOptions.medium2")}
                      </option>
                      <option value="101-500 empleados">
                        {t("demoForm.companySizeOptions.large1")}
                      </option>
                      <option value="500+ empleados">
                        {t("demoForm.companySizeOptions.large2")}
                      </option>
                    </Form.Select>
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group controlId="consultasMensuales" className="mb-3">
                    <Form.Label>{t("demoForm.monthlyQueriesLabel")}</Form.Label>
                    <Form.Select
                      className="demo-form-select"
                      value={monthlyCustomerQueries}
                      onChange={(e) =>
                        setMonthlyCustomerQueries(e.target.value)
                      }
                      required
                      disabled={isSubmitting}
                    >
                      <option value="">{t("demoForm.selectOption")}</option>
                      <option value="Menos de 100">
                        {t("demoForm.monthlyQueriesOptions.lessThan100")}
                      </option>
                      <option value="100-500">
                        {t("demoForm.monthlyQueriesOptions.between100and500")}
                      </option>
                      <option value="500-1000">
                        {t("demoForm.monthlyQueriesOptions.between500and1000")}
                      </option>
                      <option value="1000-2500">
                        {t("demoForm.monthlyQueriesOptions.between1000and2500")}
                      </option>
                      <option value="2500-5000">
                        {t("demoForm.monthlyQueriesOptions.between2500and5000")}
                      </option>
                      <option value="Más de 5000">
                        {t("demoForm.monthlyQueriesOptions.moreThan5000")}
                      </option>
                    </Form.Select>
                  </Form.Group>
                </Col>

                <Col md={12}>
                  <Form.Group controlId="websiteUrl" className="mb-3">
                    <Form.Label>{t("demoForm.websiteUrlLabel")}</Form.Label>
                    <Form.Control
                      className="demo-form-input"
                      type="text"
                      value={websiteUrl}
                      onChange={(e) => validateWebsiteUrl(e.target.value)}
                      maxLength={2048}
                      required
                      disabled={isSubmitting}
                    />
                    {websiteUrlError && (
                      <Form.Text className="text-danger">
                        {websiteUrlError}
                      </Form.Text>
                    )}
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-3" controlId="comentarios">
                <Form.Label>{t("demoForm.commentsLabel")}</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={additionalComments}
                  onChange={(e) => setAdditionalComments(e.target.value)}
                  maxLength={255}
                  placeholder={t("demoForm.commentsPlaceholder")}
                  className="demo-form-input"
                  disabled={isSubmitting}
                />
              </Form.Group>

              {errorMessage && (
                <Alert variant="danger" className="mb-2">
                  <FontAwesomeIcon icon={faTimesCircle} className="me-2" />
                  {errorMessage}
                </Alert>
              )}

              <Button
                type="submit"
                className="demo-form-btn w-100 mt-3"
                disabled={
                  isSubmitting ||
                  fullNameError !== "" ||
                  phoneError !== "" ||
                  websiteUrlError !== ""
                }
              >
                {isSubmitting ? (
                  t("demoForm.submitting")
                ) : (
                  <>
                    <FontAwesomeIcon icon={faPaperPlane} className="me-2" />
                    {t("demoForm.submitButton")}
                  </>
                )}
              </Button>

              <div className="text-center mt-3">
                <p>
                  <FontAwesomeIcon icon={faLock} className="me-1" />
                  {t("demoForm.dataPrivacy")}
                </p>
                <p>
                  <FontAwesomeIcon icon={faClock} className="me-1" />
                  {t("demoForm.contactTime")}
                </p>
              </div>
            </Form>
          </>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default DemoForm;
